import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Verify() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking"); // checking | success | error
  const [message, setMessage] = useState("");
  const { user, setUser } = useAuth();

  useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("Missing verification token.");
      return;
    }
    let alive = true;
    api
      .post("/auth/verify-email", { token })
      .then(() => {
        if (!alive) return;
        setState("success");
        setMessage("Your email address has been verified.");
        if (user && typeof user === "object") {
          setUser({ ...user, email_verified: true });
        }
      })
      .catch((err) => {
        if (!alive) return;
        setState("error");
        setMessage(
          formatApiErrorDetail(err.response?.data?.detail) ||
            "This verification link is invalid or has already been used."
        );
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" style={{ background: "#f7f2ec" }} data-testid="verify-page">
      <div className="w-full max-w-md bg-white border border-line px-8 py-12 text-center">
        <p className="overline text-gold-deep mb-6" data-testid="verify-brand">Amraiya</p>

        {state === "checking" && (
          <div data-testid="verify-checking">
            <Loader2 className="mx-auto mb-5 animate-spin" size={32} strokeWidth={1.4} />
            <h1 className="font-serif text-3xl md:text-4xl mb-3">Verifying…</h1>
            <p className="text-sm text-ink-soft">One moment while we confirm your email.</p>
          </div>
        )}

        {state === "success" && (
          <div data-testid="verify-success">
            <CheckCircle2 className="mx-auto mb-5 text-emerald-700" size={38} strokeWidth={1.4} />
            <h1 className="font-serif text-3xl md:text-4xl mb-3">Email verified</h1>
            <p className="text-sm text-ink-soft mb-8">{message}</p>
            <Link
              to="/shop"
              className="inline-block btn-ink px-8 py-3 overline"
              data-testid="verify-continue-shop"
            >
              Continue Shopping
            </Link>
          </div>
        )}

        {state === "error" && (
          <div data-testid="verify-error">
            <XCircle className="mx-auto mb-5 text-red-700" size={38} strokeWidth={1.4} />
            <h1 className="font-serif text-3xl md:text-4xl mb-3">Verification failed</h1>
            <p className="text-sm text-ink-soft mb-8">{message}</p>
            <Link
              to="/account"
              className="inline-block btn-outline px-8 py-3 overline"
              data-testid="verify-goto-account"
            >
              Go to your account
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
